import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@repo/ui/src/components/ui/button';
import { Input } from '@repo/ui/src/components/ui/input';
import { Label } from '@repo/ui/src/components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@repo/ui/src/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@repo/ui/src/components/ui/alert-dialog';
import type { ScheduleTemplateSummary } from '@repo/shared-types';
import {
  useCreateScheduleTemplate,
  useRenameScheduleTemplate,
  useDeleteScheduleTemplate,
} from '../../hooks/useScheduleTemplates';

// Three small dialogs behind "Mẫu lịch học" — each owns its own open state
// via props, same shape as SlotFormDialog/SlotActionsMenu's own confirm.

export function CreateTemplateDialog({
  open, onOpenChange, onCreated,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  /** Fired after a successful create so the caller can select the new
   *  template (`setTemplateId(created.id)`). */
  onCreated: (id: string) => void;
}) {
  const { t } = useTranslation('scheduling');
  const create = useCreateScheduleTemplate();
  const [name, setName] = useState('');
  // Default true — copying the current class assignment "is what the
  // teacher wants nine times in ten" (spec).
  const [copyFromCurrent, setCopyFromCurrent] = useState(true);

  const reset = () => { setName(''); setCopyFromCurrent(true); };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    create.mutate(
      { name: trimmed, copyFromCurrent },
      {
        onSuccess: (created) => {
          onOpenChange(false);
          reset();
          onCreated(created.id);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader><DialogTitle>{t('template.create.title')}</DialogTitle></DialogHeader>
        <form onSubmit={submit}>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label htmlFor="templateName">{t('template.create.nameLabel')}</Label>
              <Input
                id="templateName"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('template.create.namePlaceholder')}
                autoFocus
              />
            </div>
            <div className="grid gap-2">
              <Label>{t('template.create.modeLabel')}</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant={!copyFromCurrent ? 'default' : 'outline'}
                  onClick={() => setCopyFromCurrent(false)}
                  className="min-h-[44px] sm:h-8 sm:min-h-0"
                >
                  {t('template.create.empty')}
                </Button>
                <Button
                  type="button"
                  variant={copyFromCurrent ? 'default' : 'outline'}
                  onClick={() => setCopyFromCurrent(true)}
                  className="min-h-[44px] sm:h-8 sm:min-h-0"
                >
                  {t('template.create.copy')}
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t('slot.cancel')}</Button>
            <Button type="submit" disabled={create.isPending || !name.trim()}>{t('template.create.submit')}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function RenameTemplateDialog({
  open, onOpenChange, template,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  template: ScheduleTemplateSummary | null;
}) {
  const { t } = useTranslation('scheduling');
  const rename = useRenameScheduleTemplate();
  const [name, setName] = useState(template?.name ?? '');

  // Resync the field whenever a (possibly different) template is opened —
  // same "adjust state during render" pattern AssignDialog uses for its own
  // per-open reset, rather than an effect.
  const [prevTemplateId, setPrevTemplateId] = useState(template?.id ?? null);
  if (template && template.id !== prevTemplateId) {
    setPrevTemplateId(template.id);
    setName(template.name);
  }

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!template || !trimmed) return;
    rename.mutate({ id: template.id, name: trimmed }, { onSuccess: () => onOpenChange(false) });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[380px]">
        <DialogHeader><DialogTitle>{t('template.renameDialog.title')}</DialogTitle></DialogHeader>
        <form onSubmit={submit}>
          <div className="grid gap-2 py-2">
            <Label htmlFor="renameTemplateName">{t('template.create.nameLabel')}</Label>
            <Input id="renameTemplateName" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t('slot.cancel')}</Button>
            <Button type="submit" disabled={rename.isPending || !name.trim()}>{t('template.renameDialog.submit')}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteTemplateDialog({
  open, onOpenChange, template, onDeleted,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  template: ScheduleTemplateSummary | null;
  /** Fired after a successful delete — the caller exits template mode
   *  (`setTemplateId(null)`). */
  onDeleted: () => void;
}) {
  const { t } = useTranslation('scheduling');
  const del = useDeleteScheduleTemplate();

  const handleDelete = () => {
    if (!template) return;
    del.mutate(template.id, { onSuccess: () => { onOpenChange(false); onDeleted(); } });
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('template.deleteConfirm.title')}</AlertDialogTitle>
          <AlertDialogDescription>{t('template.deleteConfirm.body')}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('slot.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {t('template.deleteConfirm.action')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
